import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Package, Coins, Tag, Scale, FileSpreadsheet, FileText, Columns3, ChevronDown, X, Search,
  ArrowUp, ArrowDown, ArrowUpDown, RefreshCw, Filter, Layers
} from 'lucide-react';
import { API_URL, API_HOST } from '../../config/api';
import { exportSectionedReportToExcel } from '../../utils/reportExport';
import './CustomReport.css';

const COLS_STORAGE_KEY = 'customReport.visibleColumns.v1';
const PAGE_SIZE = 100;

const METAL_OPTIONS = [
  { id: 'gold', name: 'Altın' },
  { id: 'silver', name: 'Gümüş' },
  { id: 'brands', name: 'Markalar' },
  { id: 'steel', name: 'Çelik' },
  { id: 'zippo', name: 'Zippo' },
  { id: 'aksesuar', name: 'Aksesuar' },
  { id: 'sarf', name: 'Sarf' },
];
const METAL_LABEL = Object.fromEntries(METAL_OPTIONS.map(m => [m.id, m.name]));

const GROUP_OPTIONS = [
  { id: 'none', name: 'Gruplama Yok' },
  { id: 'brand', name: 'Marka' },
  { id: 'category', name: 'Kategori' },
  { id: 'brand_category', name: 'Marka + Kategori' },
  { id: 'supplier', name: 'Tedarikçi' },
  { id: 'store', name: 'Mağaza' },
  { id: 'metal', name: 'Ürün Türü' },
  { id: 'product', name: 'Ürün' },
  { id: 'month', name: 'Ay' },
];

const fmtDate = (d) => {
  if (!d) return '-';
  const x = new Date(d);
  return isNaN(x) ? '-' : x.toLocaleDateString('tr-TR');
};
const fmtDateTime = (d) => {
  if (!d) return '-';
  const x = new Date(d);
  return isNaN(x) ? '-' : `${x.toLocaleDateString('tr-TR')} ${x.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
};
const fmtNum = (n, digits = 2) => (n == null ? '-' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits }));
const fmtGram = (g) => (g == null ? '-' : `${fmtNum(g, 2)} gr`);
const fmtPct = (p) => (p == null ? '-' : `%${fmtNum(p, 2)}`);
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Sütun tanımları: get = ham değer (sıralama/Excel), fmt = ekranda/PDF'te gösterim
const buildColumns = (formatCurrency) => [
  { key: 'image', label: 'Görsel', def: true, noExcel: true, noSort: true },
  { key: 'barcode', label: 'Barkod', def: true, get: r => r.barcode || '' },
  { key: 'product_name', label: 'Ürün Adı', def: true, get: r => r.product_name || '' },
  { key: 'brand', label: 'Marka', def: true, get: r => r.brand || '' },
  { key: 'category', label: 'Kategori', def: true, get: r => r.category || '' },
  { key: 'supplier', label: 'Tedarikçi', def: true, get: r => r.supplier || '' },
  { key: 'metal_type', label: 'Ürün Türü', def: false, get: r => METAL_LABEL[r.metal_type] || r.metal_type || '' },
  { key: 'weight', label: 'Gramaj', def: true, num: true, get: r => r.weight, fmt: r => fmtGram(r.weight) },
  { key: 'karat', label: 'Ayar', def: true, get: r => r.karat || '' },
  { key: 'labored_gram', label: 'İşçilikli Gram', def: false, num: true, get: r => r.labored_gram, fmt: r => fmtGram(r.labored_gram) },
  { key: 'has_gram', label: 'Has Gram', def: false, num: true, get: r => r.has_gram, fmt: r => fmtGram(r.has_gram) },
  { key: 'stock_entry_date', label: 'Stok Giriş Tarihi', def: false, date: true, get: r => r.stock_entry_date, fmt: r => fmtDate(r.stock_entry_date) },
  { key: 'sale_date', label: 'Satış Tarihi', def: true, date: true, get: r => r.sale_date, fmt: r => fmtDateTime(r.sale_date) },
  { key: 'store_name', label: 'Mağaza', def: false, get: r => r.store_name || '' },
  { key: 'receipt_number', label: 'Fiş No', def: false, get: r => String(r.receipt_number || ''), fmt: r => `#${r.receipt_number}` },
  { key: 'staff_name', label: 'Personel', def: false, get: r => r.staff_name || '' },
  { key: 'type', label: 'İşlem', def: false, get: r => (r.type === 'refund' ? 'İade' : 'Satış') },
  { key: 'quantity', label: 'Adet', def: true, num: true, get: r => r.quantity, fmt: r => String(r.quantity) },
  { key: 'normal_price', label: 'Normal Fiyat', def: true, num: true, money: true, get: r => r.normal_price, fmt: r => formatCurrency(r.normal_price) },
  { key: 'discount_amount', label: 'İndirim Tutarı', def: true, num: true, money: true, get: r => r.discount_amount, fmt: r => (r.discount_amount ? formatCurrency(r.discount_amount) : '-') },
  { key: 'discount_percent', label: 'İndirim %', def: true, num: true, get: r => r.discount_percent, fmt: r => (r.discount_percent ? fmtPct(r.discount_percent) : '-') },
  { key: 'sold_price', label: 'Satış Fiyatı', def: true, num: true, money: true, get: r => r.sold_price, fmt: r => formatCurrency(r.sold_price) },
];

const groupKeyOf = (r, mode) => {
  switch (mode) {
    case 'brand': return r.brand || 'Markasız';
    case 'category': return r.category || 'Kategorisiz';
    case 'brand_category': return `${r.brand || 'Markasız'} › ${r.category || 'Kategorisiz'}`;
    case 'supplier': return r.supplier || 'Tedarikçi Yok';
    case 'store': return r.store_name || '-';
    case 'metal': return METAL_LABEL[r.metal_type] || r.metal_type || 'Diğer';
    case 'product': return r.barcode ? `${r.product_name} (${r.barcode})` : r.product_name;
    case 'month': {
      const d = new Date(r.sale_date);
      return isNaN(d) ? '-' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
    default: return '';
  }
};

const summarize = (list) => {
  const t = { lines: 0, qty: 0, weight: 0, has: 0, labored: 0, normal: 0, discount: 0, sold: 0 };
  for (const r of list) {
    t.lines += 1;
    t.qty += Number(r.quantity) || 0;
    t.weight += Number(r.weight) || 0;
    t.has += Number(r.has_gram) || 0;
    t.labored += Number(r.labored_gram) || 0;
    t.normal += Number(r.normal_price) || 0;
    t.discount += Number(r.discount_amount) || 0;
    t.sold += Number(r.sold_price) || 0;
  }
  t.discountPct = t.normal > 0 ? (t.discount / t.normal) * 100 : 0;
  return t;
};

// Aranabilir çoklu seçim
export const MultiSelect = ({ label, options, selected, onChange, placeholder = 'Tümü', renderOption }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const filtered = useMemo(() => {
    const s = q.trim().toLocaleLowerCase('tr');
    return s ? options.filter(o => o.toLocaleLowerCase('tr').includes(s)) : options;
  }, [options, q]);
  const selSet = useMemo(() => new Set(selected), [selected]);
  const toggle = (o) => onChange(selSet.has(o) ? selected.filter(x => x !== o) : [...selected, o]);
  const summary = selected.length === 0 ? placeholder
    : selected.length <= 2 ? selected.join(', ') : `${selected.length} seçili`;
  return (
    <div className="cr-ms" ref={ref}>
      <label className="cr-field-label">{label}</label>
      <button type="button" className={`cr-ms-trigger ${selected.length ? 'has-value' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className="cr-ms-summary">{summary}</span>
        {selected.length > 0 && (
          <span className="cr-ms-clear" onClick={(e) => { e.stopPropagation(); onChange([]); }} title="Temizle"><X size={13} /></span>
        )}
        <ChevronDown size={15} />
      </button>
      {open && (
        <div className="cr-ms-pop">
          <div className="cr-ms-search">
            <Search size={14} />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Ara..." />
          </div>
          <div className="cr-ms-actions">
            <button type="button" onClick={() => onChange([...new Set([...selected, ...filtered])])}>Görünenleri seç</button>
            <button type="button" onClick={() => onChange([])}>Temizle</button>
          </div>
          <div className="cr-ms-list">
            {filtered.length === 0 && <div className="cr-ms-empty">Sonuç yok</div>}
            {filtered.slice(0, 500).map(o => (
              <label key={o} className="cr-ms-item">
                <input type="checkbox" checked={selSet.has(o)} onChange={() => toggle(o)} />
                <span>{renderOption ? renderOption(o) : o}</span>
              </label>
            ))}
            {filtered.length > 500 && <div className="cr-ms-empty">İlk 500 gösteriliyor, aramayı daraltın</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomReport = ({ getDateRange, reportStoreFilter, formatCurrency, getPeriodLabel, getStoreLabel }) => {
  const COLUMNS = useMemo(() => buildColumns(formatCurrency), [formatCurrency]);

  const [options, setOptions] = useState({ brands: [], categories: [], brandCategories: {}, suppliers: [] });
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pairs, setPairs] = useState([]); // [{brand, category}]
  const [pairBrand, setPairBrand] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [metals, setMetals] = useState([]);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [includeRefunds, setIncludeRefunds] = useState(true);

  const [items, setItems] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [groupBy, setGroupBy] = useState('brand');
  const [groupSort, setGroupSort] = useState({ field: 'sold', dir: 'desc' });
  const [groupFocus, setGroupFocus] = useState(null); // tabloyu tek gruba daralt
  const [sort, setSort] = useState({ field: 'sale_date', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [colsOpen, setColsOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const colsRef = useRef(null);

  const [visible, setVisible] = useState(() => {
    const defaults = Object.fromEntries(buildColumns(() => '').map(c => [c.key, c.def]));
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_STORAGE_KEY) || 'null');
      if (saved && typeof saved === 'object') return { ...defaults, ...saved };
    } catch (_) { /* yok say */ }
    return defaults;
  });
  useEffect(() => {
    try { localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(visible)); } catch (_) { /* yok say */ }
  }, [visible]);
  useEffect(() => {
    if (!colsOpen) return;
    const h = (e) => { if (colsRef.current && !colsRef.current.contains(e.target)) setColsOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [colsOpen]);

  useEffect(() => {
    fetch(`${API_URL}/reports/custom-sales/options`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setOptions(d); })
      .catch(e => console.error(e));
  }, []);

  // Ürün türü seçiliyse Marka / Kategori / Tedarikçi / Marka+Kategori seçenekleri sadece o türlerden gelsin
  const visibleOptions = useMemo(() => {
    if (!metals.length || !Array.isArray(options.combos)) return options;
    const metalSet = new Set(metals);
    const combos = options.combos.filter(c => metalSet.has(c.metal_type));
    const sortTr = (a, b) => a.localeCompare(b, 'tr');
    const brandCategories = {};
    for (const c of combos) {
      if (!c.brand || !c.category) continue;
      const list = (brandCategories[c.brand] = brandCategories[c.brand] || []);
      if (!list.includes(c.category)) list.push(c.category);
    }
    Object.values(brandCategories).forEach(list => list.sort(sortTr));
    return {
      ...options,
      brands: [...new Set(combos.map(c => c.brand).filter(Boolean))].sort(sortTr),
      categories: [...new Set(combos.map(c => c.category).filter(Boolean))].sort(sortTr),
      brandCategories,
      suppliers: [...new Set((options.supplierMetals || []).filter(r => metalSet.has(r.metal_type)).map(r => r.supplier))]
        .sort((a, b) => a.localeCompare(b, 'tr', { numeric: true })),
    };
  }, [options, metals]);

  // Tür değişince artık listede olmayan seçimleri bırak (gizli seçim sonucu boşaltmasın)
  useEffect(() => {
    const keep = (sel, list) => {
      const set = new Set(list || []);
      const next = sel.filter(x => set.has(x));
      return next.length === sel.length ? sel : next;
    };
    setBrands(prev => keep(prev, visibleOptions.brands));
    setCategories(prev => keep(prev, visibleOptions.categories));
    setSuppliers(prev => keep(prev, visibleOptions.suppliers));
    setPairs(prev => {
      const next = prev.filter(p => (visibleOptions.brandCategories?.[p.brand] || []).includes(p.category));
      return next.length === prev.length ? prev : next;
    });
    setPairBrand(prev => (prev && !visibleOptions.brandCategories?.[prev]?.length ? '' : prev));
  }, [visibleOptions]);

  // Arama kutusu yazarken her tuşta sorgu atmasın
  useEffect(() => {
    const t = setTimeout(() => setSearchApplied(search), 450);
    return () => clearTimeout(t);
  }, [search]);

  const reqIdRef = useRef(0);
  const fetchData = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const dr = getDateRange();
      const res = await fetch(`${API_URL}/reports/custom-sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: dr.start,
          end_date: dr.end,
          store_filter: reportStoreFilter,
          brands, categories, brand_categories: pairs, suppliers, metal_types: metals,
          search: searchApplied,
          include_refunds: includeRefunds
        })
      });
      const data = await res.json();
      if (reqId !== reqIdRef.current) return;
      if (!res.ok) throw new Error(data?.error || 'Rapor alınamadı');
      setItems(data.items || []);
      setTruncated(!!data.truncated);
      setPage(1);
      setGroupFocus(null);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      console.error(e);
      setError(e.message);
      setItems([]);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [getDateRange, reportStoreFilter, brands, categories, pairs, suppliers, metals, searchApplied, includeRefunds]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeColumns = COLUMNS.filter(c => visible[c.key]);

  // Gruplar
  const groups = useMemo(() => {
    if (groupBy === 'none') return [];
    const map = new Map();
    for (const r of items) {
      const k = groupKeyOf(r, groupBy);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    const totalSold = items.reduce((s, r) => s + (Number(r.sold_price) || 0), 0);
    const arr = [...map.entries()].map(([key, list]) => {
      const s = summarize(list);
      return { key, ...s, share: totalSold ? (s.sold / totalSold) * 100 : 0 };
    });
    const { field, dir } = groupSort;
    const m = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (field === 'key') return m * String(a.key).localeCompare(String(b.key), 'tr', { numeric: true });
      return m * ((a[field] || 0) - (b[field] || 0));
    });
    return arr;
  }, [items, groupBy, groupSort]);

  // Detay satırları (grup odağı + sıralama)
  const detailRows = useMemo(() => {
    let list = groupFocus != null ? items.filter(r => groupKeyOf(r, groupBy) === groupFocus) : items;
    const col = COLUMNS.find(c => c.key === sort.field);
    if (col && col.get) {
      const m = sort.dir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        let va = col.get(a), vb = col.get(b);
        if (col.date) { va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0; }
        if (col.num || col.date) return m * ((Number(va) || 0) - (Number(vb) || 0));
        return m * String(va).localeCompare(String(vb), 'tr', { numeric: true });
      });
    }
    return list;
  }, [items, groupFocus, groupBy, sort, COLUMNS]);

  const totals = useMemo(() => summarize(items), [items]);
  const detailTotals = useMemo(() => summarize(detailRows), [detailRows]);
  const pageCount = Math.max(1, Math.ceil(detailRows.length / PAGE_SIZE));
  const pageRows = detailRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field) => {
    setSort(s => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' }));
    setPage(1);
  };
  const toggleGroupSort = (field) => {
    setGroupSort(s => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: field === 'key' ? 'asc' : 'desc' }));
  };
  const SortIcon = ({ active, dir }) => (active ? (dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="cr-sort-idle" />);

  // Toplam satırında gösterilecek değer
  const totalCell = (key, t) => {
    switch (key) {
      case 'quantity': return String(t.qty);
      case 'weight': return fmtGram(t.weight);
      case 'labored_gram': return fmtGram(t.labored);
      case 'has_gram': return fmtGram(t.has);
      case 'normal_price': return formatCurrency(t.normal);
      case 'discount_amount': return formatCurrency(t.discount);
      case 'discount_percent': return fmtPct(t.discountPct);
      case 'sold_price': return formatCurrency(t.sold);
      default: return '';
    }
  };

  const activeFilterCount = brands.length + categories.length + pairs.length + suppliers.length + metals.length + (searchApplied.trim() ? 1 : 0);
  const clearFilters = () => {
    setBrands([]); setCategories([]); setPairs([]); setPairBrand(''); setSuppliers([]); setMetals([]); setSearch('');
  };

  const filterSummaryText = () => {
    const parts = [];
    if (metals.length) parts.push(`Tür: ${metals.map(m => METAL_LABEL[m] || m).join(', ')}`);
    if (brands.length) parts.push(`Marka: ${brands.join(', ')}`);
    if (categories.length) parts.push(`Kategori: ${categories.join(', ')}`);
    if (pairs.length) parts.push(`Marka+Kategori: ${pairs.map(p => `${p.brand} ${p.category}`).join(', ')}`);
    if (suppliers.length) parts.push(`Tedarikçi: ${suppliers.join(', ')}`);
    if (searchApplied.trim()) parts.push(`Arama: "${searchApplied.trim()}"`);
    if (groupFocus != null) parts.push(`Grup: ${groupFocus}`);
    parts.push(includeRefunds ? 'İadeler düşülmüş' : 'İadeler hariç');
    return parts.join(' | ');
  };

  const groupLabel = GROUP_OPTIONS.find(g => g.id === groupBy)?.name || '';

  // ---- EXCEL ----
  const exportExcel = () => {
    const cols = activeColumns.filter(c => !c.noExcel);
    const excelVal = (c, r) => {
      if (c.money || c.key === 'quantity') return Number(c.get(r)) || 0;
      if (c.num) { const v = c.get(r); return v == null ? '' : Number(v); }
      if (c.date) return c.fmt(r);
      return c.get(r);
    };
    const moneyIdx = cols.map((c, i) => (c.money ? i : -1)).filter(i => i >= 0);
    const totalRow = cols.map((c, i) => {
      if (i === 0) return 'TOPLAM';
      switch (c.key) {
        case 'quantity': return detailTotals.qty;
        case 'weight': return Number(detailTotals.weight.toFixed(3));
        case 'labored_gram': return Number(detailTotals.labored.toFixed(3));
        case 'has_gram': return Number(detailTotals.has.toFixed(3));
        case 'normal_price': return detailTotals.normal;
        case 'discount_amount': return detailTotals.discount;
        case 'discount_percent': return Number(detailTotals.discountPct.toFixed(2));
        case 'sold_price': return detailTotals.sold;
        default: return '';
      }
    });
    const sections = [];
    if (groupBy !== 'none' && groups.length) {
      sections.push({
        heading: `${groupLabel} Özeti`,
        columns: [groupLabel, 'Satır', 'Adet', 'Gramaj (gr)', 'Has Gram', 'Normal Fiyat', 'İndirim', 'İndirim %', 'Satış Tutarı', 'Pay %'],
        numberCols: [5, 6, 8],
        colWidths: [{ wch: 36 }, { wch: 8 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 8 }],
        rows: [
          ...groups.map(g => [g.key, g.lines, g.qty, Number(g.weight.toFixed(3)), Number(g.has.toFixed(3)), g.normal, g.discount, Number(g.discountPct.toFixed(2)), g.sold, Number(g.share.toFixed(2))]),
          { kind: 'total', span: 1, cells: ['TOPLAM', totals.lines, totals.qty, Number(totals.weight.toFixed(3)), Number(totals.has.toFixed(3)), totals.normal, totals.discount, Number(totals.discountPct.toFixed(2)), totals.sold, 100] }
        ]
      });
    }
    sections.push({
      heading: 'Satış Detayı',
      columns: cols.map(c => c.label),
      numberCols: moneyIdx,
      colWidths: cols.map(c => ({ wch: c.key === 'product_name' ? 34 : c.key === 'sale_date' ? 17 : c.money ? 15 : 13 })),
      rows: [
        ...detailRows.map(r => cols.map(c => excelVal(c, r))),
        { kind: 'total', span: 1, cells: totalRow }
      ]
    });
    sections.push({
      heading: 'Filtreler',
      columns: ['Filtre'],
      colWidths: [{ wch: 120 }],
      rows: filterSummaryText().split(' | ').map(s => [s])
    });
    exportSectionedReportToExcel({
      title: 'Özel Rapor',
      periodLabel: getPeriodLabel ? getPeriodLabel() : '',
      storeLabel: getStoreLabel ? getStoreLabel() : '',
      sections
    });
  };

  // ---- PDF (yazdır → PDF olarak kaydet; Türkçe karakter ve ürün görselleri korunur) ----
  const exportPdf = () => {
    const cols = activeColumns;
    const w = window.open('', '_blank');
    if (!w) { alert('Açılır pencere engellendi. Lütfen tarayıcıda izin verin.'); return; }
    const cell = (c, r) => {
      if (c.key === 'image') {
        return r.image ? `<img src="${escapeHtml(API_HOST + r.image)}" alt="" />` : '';
      }
      return escapeHtml(c.fmt ? c.fmt(r) : (c.get(r) || '-'));
    };
    const groupHtml = (groupBy !== 'none' && groups.length) ? `
      <h2>${escapeHtml(groupLabel)} Özeti</h2>
      <table>
        <thead><tr><th>${escapeHtml(groupLabel)}</th><th class="r">Adet</th><th class="r">Gramaj</th><th class="r">Has Gram</th><th class="r">Normal Fiyat</th><th class="r">İndirim</th><th class="r">İnd. %</th><th class="r">Satış Tutarı</th><th class="r">Pay</th></tr></thead>
        <tbody>
          ${groups.map(g => `<tr><td>${escapeHtml(g.key)}</td><td class="r">${g.qty}</td><td class="r">${fmtGram(g.weight)}</td><td class="r">${fmtGram(g.has)}</td><td class="r">${escapeHtml(formatCurrency(g.normal))}</td><td class="r">${escapeHtml(formatCurrency(g.discount))}</td><td class="r">${fmtPct(g.discountPct)}</td><td class="r"><b>${escapeHtml(formatCurrency(g.sold))}</b></td><td class="r">${fmtPct(g.share)}</td></tr>`).join('')}
          <tr class="tot"><td>TOPLAM</td><td class="r">${totals.qty}</td><td class="r">${fmtGram(totals.weight)}</td><td class="r">${fmtGram(totals.has)}</td><td class="r">${escapeHtml(formatCurrency(totals.normal))}</td><td class="r">${escapeHtml(formatCurrency(totals.discount))}</td><td class="r">${fmtPct(totals.discountPct)}</td><td class="r">${escapeHtml(formatCurrency(totals.sold))}</td><td class="r">%100</td></tr>
        </tbody>
      </table>` : '';
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Özel Rapor - ${escapeHtml(getPeriodLabel ? getPeriodLabel() : '')}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #1e293b; margin: 18px; font-size: 10px; }
        .hdr { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #0f766e; padding-bottom: 8px; margin-bottom: 10px; }
        .hdr h1 { margin: 0; font-size: 20px; color: #0f766e; }
        .meta { font-size: 10px; color: #475569; line-height: 1.5; text-align: right; }
        .filters { background: #f1f5f9; border-left: 3px solid #0f766e; padding: 6px 10px; margin-bottom: 10px; font-size: 9.5px; color: #334155; }
        .kpis { display: flex; gap: 8px; margin-bottom: 12px; }
        .kpi { flex: 1; border: 1px solid #cbd5e1; border-radius: 6px; padding: 6px 8px; }
        .kpi b { display: block; font-size: 13px; color: #0f766e; }
        .kpi span { font-size: 9px; color: #64748b; }
        h2 { font-size: 13px; margin: 14px 0 6px; color: #334155; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #0f766e; color: #fff; padding: 5px 4px; font-size: 9px; text-align: left; }
        td { padding: 4px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
        tr:nth-child(even) td { background: #f8fafc; }
        .r { text-align: right; white-space: nowrap; }
        .tot td { background: #ccfbf1 !important; font-weight: 700; border-top: 2px solid #0f766e; }
        .refund td { color: #b91c1c; }
        img { width: 36px; height: 36px; object-fit: cover; border-radius: 4px; display: block; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        @page { size: A4 landscape; margin: 10mm; }
      </style></head><body>
      <div class="hdr"><h1>AKİK · Özel Rapor</h1>
        <div class="meta">Dönem: <b>${escapeHtml(getPeriodLabel ? getPeriodLabel() : '-')}</b><br/>Mağaza: <b>${escapeHtml(getStoreLabel ? getStoreLabel() : '-')}</b><br/>Oluşturulma: ${escapeHtml(fmtDateTime(new Date()))}</div>
      </div>
      <div class="filters">${escapeHtml(filterSummaryText())}</div>
      <div class="kpis">
        <div class="kpi"><b>${detailTotals.qty}</b><span>Satılan Adet</span></div>
        <div class="kpi"><b>${escapeHtml(formatCurrency(detailTotals.sold))}</b><span>Satış Tutarı</span></div>
        <div class="kpi"><b>${escapeHtml(formatCurrency(detailTotals.normal))}</b><span>Normal Fiyat Toplamı</span></div>
        <div class="kpi"><b>${escapeHtml(formatCurrency(detailTotals.discount))} (${fmtPct(detailTotals.discountPct)})</b><span>Toplam İndirim</span></div>
        <div class="kpi"><b>${fmtGram(detailTotals.weight)}</b><span>Toplam Gramaj</span></div>
      </div>
      ${groupFocus == null ? groupHtml : ''}
      <h2>Satış Detayı (${detailRows.length} satır)</h2>
      <table>
        <thead><tr>${cols.map(c => `<th class="${c.num ? 'r' : ''}">${escapeHtml(c.label)}</th>`).join('')}</tr></thead>
        <tbody>
          ${detailRows.map(r => `<tr class="${r.type === 'refund' ? 'refund' : ''}">${cols.map(c => `<td class="${c.num ? 'r' : ''}">${cell(c, r)}</td>`).join('')}</tr>`).join('')}
          <tr class="tot">${cols.map((c, i) => `<td class="${c.num ? 'r' : ''}">${i === 0 ? 'TOPLAM' : escapeHtml(totalCell(c.key, detailTotals))}</td>`).join('')}</tr>
        </tbody>
      </table>
      <script>
        (function () {
          var imgs = Array.prototype.slice.call(document.images);
          var left = imgs.length, done = false;
          function go() { if (done) return; done = true; setTimeout(function () { window.print(); }, 150); }
          if (!left) return go();
          imgs.forEach(function (im) {
            if (im.complete) { if (--left === 0) go(); }
            else { im.onload = im.onerror = function () { if (--left === 0) go(); }; }
          });
          setTimeout(go, 15000);
        })();
      </script>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const brandCatsForPair = pairBrand ? (visibleOptions.brandCategories?.[pairBrand] || []) : [];
  const pairSet = new Set(pairs.map(p => `${p.brand}\u0000${p.category}`));
  const togglePair = (category) => {
    const k = `${pairBrand}\u0000${category}`;
    setPairs(pairSet.has(k) ? pairs.filter(p => `${p.brand}\u0000${p.category}` !== k) : [...pairs, { brand: pairBrand, category }]);
  };

  return (
    <div className="tab-content cr-root">
      {/* FİLTRELER */}
      <div className="data-card cr-filters">
        <div className="cr-filters-head">
          <h3><Filter size={16} /> Filtreler {activeFilterCount > 0 && <span className="cr-badge">{activeFilterCount}</span>}</h3>
          <div className="cr-filters-head-actions">
            {activeFilterCount > 0 && <button className="cr-link-btn" onClick={clearFilters}>Filtreleri temizle</button>}
            <button className="cr-icon-btn" onClick={fetchData} title="Yenile" disabled={loading}><RefreshCw size={15} className={loading ? 'cr-spin' : ''} /></button>
          </div>
        </div>
        <p className="cr-hint">Dönem ve mağaza seçimi yukarıdaki genel filtreden gelir.</p>

        <div className="cr-metal-row">
          <label className="cr-field-label">Ürün Türü</label>
          <div className="cr-chips">
            {METAL_OPTIONS.map(m => (
              <button key={m.id} type="button"
                className={`cr-chip ${metals.includes(m.id) ? 'active' : ''}`}
                onClick={() => setMetals(metals.includes(m.id) ? metals.filter(x => x !== m.id) : [...metals, m.id])}>
                {m.name}
              </button>
            ))}
          </div>
        </div>

        <div className="cr-filter-grid">
          <MultiSelect label="Marka" options={visibleOptions.brands || []} selected={brands} onChange={setBrands} />
          <MultiSelect label="Kategori" options={visibleOptions.categories || []} selected={categories} onChange={setCategories} />
          <MultiSelect label="Tedarikçi" options={visibleOptions.suppliers || []} selected={suppliers} onChange={setSuppliers} />
          <div className="cr-field">
            <label className="cr-field-label">Ürün Ara</label>
            <div className="cr-search">
              <Search size={14} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Ürün adı veya barkod" />
              {search && <X size={14} className="cr-search-clear" onClick={() => setSearch('')} />}
            </div>
          </div>
        </div>

        <div className="cr-pair-box">
          <label className="cr-field-label"><Layers size={13} /> Marka + Alt Kategori <span className="cr-muted">(örn. Guess Bileklik, Guess Kolye)</span></label>
          <div className="cr-pair-row">
            <select value={pairBrand} onChange={e => setPairBrand(e.target.value)} className="cr-select">
              <option value="">Marka seçin…</option>
              {(visibleOptions.brands || []).filter(b => visibleOptions.brandCategories?.[b]?.length).map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            {pairBrand && (
              <div className="cr-chips">
                {brandCatsForPair.map(c => (
                  <button key={c} type="button" className={`cr-chip ${pairSet.has(`${pairBrand}\u0000${c}`) ? 'active' : ''}`} onClick={() => togglePair(c)}>{c}</button>
                ))}
              </div>
            )}
          </div>
          {pairs.length > 0 && (
            <div className="cr-chips cr-selected-pairs">
              {pairs.map(p => (
                <span key={`${p.brand}-${p.category}`} className="cr-pair-tag">
                  {p.brand} › {p.category}
                  <X size={12} onClick={() => setPairs(pairs.filter(x => !(x.brand === p.brand && x.category === p.category)))} />
                </span>
              ))}
            </div>
          )}
          <p className="cr-hint">Marka ve Kategori birlikte seçilirse kesişimi (ör. Guess + Bileklik) gelir; buradaki çiftler sonuca ayrıca eklenir.</p>
        </div>

        <label className="cr-check">
          <input type="checkbox" checked={includeRefunds} onChange={e => setIncludeRefunds(e.target.checked)} />
          İadeleri düş (iade kalemleri eksi satır olarak listelenir, toplamlar net olur)
        </label>
      </div>

      {error && <div className="cr-error">Hata: {error}</div>}
      {truncated && <div className="cr-warn">Sonuç çok büyük; ilk 50.000 satır gösteriliyor. Tarih aralığını veya filtreleri daraltın.</div>}

      {/* ÖZET KARTLAR */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Package size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{loading ? '…' : totals.qty.toLocaleString('tr-TR')}</div>
            <div className="stat-label">Satılan Adet</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Coins size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{loading ? '…' : formatCurrency(totals.sold)}</div>
            <div className="stat-label">Satış Tutarı</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Tag size={20} style={{ color: '#7c3aed' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{loading ? '…' : formatCurrency(totals.discount)}</div>
            <div className="stat-label">Toplam İndirim ({fmtPct(totals.discountPct)})</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><Scale size={20} style={{ color: '#b45309' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{loading ? '…' : fmtGram(totals.weight)}</div>
            <div className="stat-label">Toplam Gramaj · Has {fmtGram(totals.has)}</div>
          </div>
        </div>
      </div>

      {/* GRUP ÖZETİ */}
      <div className="data-card">
        <div className="cr-card-head">
          <h3>Özet</h3>
          <div className="cr-chips">
            {GROUP_OPTIONS.map(g => (
              <button key={g.id} type="button" className={`cr-chip ${groupBy === g.id ? 'active' : ''}`}
                onClick={() => { setGroupBy(g.id); setGroupFocus(null); setPage(1); }}>{g.name}</button>
            ))}
          </div>
        </div>
        {groupBy !== 'none' && (
          <div className="table-wrapper cr-group-table">
            <table className="data-table">
              <thead>
                <tr>
                  {[['key', groupLabel], ['qty', 'Adet'], ['weight', 'Gramaj'], ['has', 'Has Gram'], ['normal', 'Normal Fiyat'], ['discount', 'İndirim'], ['discountPct', 'İnd. %'], ['sold', 'Satış Tutarı'], ['share', 'Pay']].map(([f, l]) => (
                    <th key={f} className={`cr-th-sort ${f !== 'key' ? 'cr-num' : ''}`} onClick={() => toggleGroupSort(f)}>
                      {l} <SortIcon active={groupSort.field === f} dir={groupSort.dir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!loading && groups.length === 0 && (
                  <tr><td colSpan={9} className="cr-empty-cell">Bu filtrelerle satış bulunamadı</td></tr>
                )}
                {groups.map(g => (
                  <tr key={g.key} className={`cr-group-row ${groupFocus === g.key ? 'focused' : ''}`}
                    onClick={() => { setGroupFocus(groupFocus === g.key ? null : g.key); setPage(1); }}
                    title="Detay tablosunu bu gruba daralt">
                    <td><strong>{g.key}</strong></td>
                    <td className="cr-num">{g.qty}</td>
                    <td className="cr-num">{fmtGram(g.weight)}</td>
                    <td className="cr-num">{fmtGram(g.has)}</td>
                    <td className="cr-num">{formatCurrency(g.normal)}</td>
                    <td className="cr-num">{formatCurrency(g.discount)}</td>
                    <td className="cr-num">{fmtPct(g.discountPct)}</td>
                    <td className="cr-num"><strong>{formatCurrency(g.sold)}</strong></td>
                    <td className="cr-num">
                      <div className="cr-share"><div className="cr-share-bar" style={{ width: `${Math.max(0, Math.min(100, g.share))}%` }} /><span>{fmtPct(g.share)}</span></div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {groups.length > 0 && (
                <tfoot>
                  <tr className="cr-total-row">
                    <td>TOPLAM ({groups.length} grup)</td>
                    <td className="cr-num">{totals.qty}</td>
                    <td className="cr-num">{fmtGram(totals.weight)}</td>
                    <td className="cr-num">{fmtGram(totals.has)}</td>
                    <td className="cr-num">{formatCurrency(totals.normal)}</td>
                    <td className="cr-num">{formatCurrency(totals.discount)}</td>
                    <td className="cr-num">{fmtPct(totals.discountPct)}</td>
                    <td className="cr-num">{formatCurrency(totals.sold)}</td>
                    <td className="cr-num">%100</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* DETAY TABLOSU */}
      <div className="data-card">
        <div className="cr-card-head">
          <h3>
            Satış Detayı <span className="cr-muted">({detailRows.length.toLocaleString('tr-TR')} satır)</span>
            {groupFocus != null && (
              <span className="cr-pair-tag cr-focus-tag">{groupFocus}<X size={12} onClick={() => setGroupFocus(null)} /></span>
            )}
          </h3>
          <div className="cr-toolbar">
            <div className="cr-cols" ref={colsRef}>
              <button className="cr-btn" onClick={() => setColsOpen(o => !o)}><Columns3 size={15} /> Sütunlar ({activeColumns.length})</button>
              {colsOpen && (
                <div className="cr-cols-pop">
                  <div className="cr-ms-actions">
                    <button type="button" onClick={() => setVisible(Object.fromEntries(COLUMNS.map(c => [c.key, true])))}>Tümü</button>
                    <button type="button" onClick={() => setVisible(Object.fromEntries(COLUMNS.map(c => [c.key, c.def])))}>Varsayılan</button>
                  </div>
                  {COLUMNS.map(c => (
                    <label key={c.key} className="cr-ms-item">
                      <input type="checkbox" checked={!!visible[c.key]} onChange={() => setVisible(v => ({ ...v, [c.key]: !v[c.key] }))} />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button className="cr-btn cr-btn-excel" onClick={exportExcel} disabled={loading || !detailRows.length}><FileSpreadsheet size={15} /> Excel</button>
            <button className="cr-btn cr-btn-pdf" onClick={exportPdf} disabled={loading || !detailRows.length}><FileText size={15} /> PDF</button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table cr-detail-table">
            <thead>
              <tr>
                {activeColumns.map(c => (
                  <th key={c.key} className={`${c.noSort ? '' : 'cr-th-sort'} ${c.num ? 'cr-num' : ''}`} onClick={c.noSort ? undefined : () => toggleSort(c.key)}>
                    {c.label} {!c.noSort && <SortIcon active={sort.field === c.key} dir={sort.dir} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={activeColumns.length || 1} className="cr-empty-cell">Yükleniyor…</td></tr>
              )}
              {!loading && pageRows.length === 0 && (
                <tr><td colSpan={activeColumns.length || 1} className="cr-empty-cell">Bu filtrelerle satış bulunamadı</td></tr>
              )}
              {!loading && pageRows.map(r => (
                <tr key={r.id} className={r.type === 'refund' ? 'cr-refund-row' : ''}>
                  {activeColumns.map(c => {
                    if (c.key === 'image') {
                      return (
                        <td key={c.key} className="cr-img-cell">
                          {r.image ? (
                            <img src={`${API_HOST}${r.image}`} alt="" loading="lazy"
                              onClick={() => setImagePreview({ src: `${API_HOST}${r.image}`, name: r.product_name })} />
                          ) : <span className="cr-no-img">—</span>}
                        </td>
                      );
                    }
                    const v = c.fmt ? c.fmt(r) : (c.get(r) || '-');
                    return <td key={c.key} className={c.num ? 'cr-num' : ''}>{c.key === 'type' && r.type === 'refund' ? <span className="cr-refund-badge">İade</span> : v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
            {!loading && detailRows.length > 0 && (
              <tfoot>
                <tr className="cr-total-row">
                  {activeColumns.map((c, i) => (
                    <td key={c.key} className={c.num ? 'cr-num' : ''}>{i === 0 ? 'TOPLAM' : totalCell(c.key, detailTotals)}</td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {pageCount > 1 && (
          <div className="cr-pager">
            <button className="cr-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Önceki</button>
            <span>Sayfa {page} / {pageCount}</span>
            <button className="cr-btn" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}>Sonraki ›</button>
          </div>
        )}
      </div>

      {imagePreview && (
        <div className="cr-img-modal" onClick={() => setImagePreview(null)}>
          <div className="cr-img-modal-inner" onClick={e => e.stopPropagation()}>
            <button className="cr-img-close" onClick={() => setImagePreview(null)}><X size={18} /></button>
            <img src={imagePreview.src} alt={imagePreview.name} />
            <div>{imagePreview.name}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomReport;
